/*
 * @providesModule PLOWDisplayHandler
 *
 */

import React from 'react';

import AWSManager from 'AWSManager';
import ClientPropertyConstants from 'ClientPropertyConstants';
import CurrentUserStore from 'CurrentUserStore';
import EventDetailsScreen from 'EventDetailsScreen';
import GlobalEventEmitter from 'GlobalEventEmitter';
import I18n from 'INTL';
import LiveEventCompletedPLOW from 'LiveEventCompletedPLOW';
import LiveEventRecapPLOW from 'LiveEventRecapPLOW';
import LiveEventPendingPLOW from 'LiveEventPendingPLOW';
import LoadingIndicator from 'LoadingIndicator';
import MatchStore from 'MatchStore';
import Match from 'SKZMatch';
import MatchPlayer from 'SKZMatchPlayer';
import ModalNavigationController from 'ModalNavigationController';
import NativeNavigation from 'NativeNavigation';
import NavigationManager from 'NavigationManager';
import PlayerLevelUpController from 'PlayerLevelUpController';
import Prefs from 'Preferences';
import RegularPLOW from 'RegularPLOW';
import RematchDeclinedModal from 'RematchDeclinedModal';
import SaveAccountFlowController from 'SaveAccountFlowController';
import Sizes from 'Sizes';
import SKZConstants from 'SKZConstants';
import Tournament from 'SKZTournament';
import TournamentStore from 'TournamentStore';
import SkillzModule from 'SkillzModule';
import SkillzRouter from 'SkillzRouter';
import BracketsPLOW from 'BracketsPLOW';
import {NativeModules, Platform, StyleSheet, View} from 'react-native';
import {handleLeagueInterstitialsAndSync} from 'LeagueInfoUtils';
import LeagueFTUEController from 'LeagueFTUEController';
import SkillzSDK from 'SkillzSDK';

// Legacy type identifiers
const LegacyTournamentTypeLimited = 0;
const LegacyTournamentTypeUnlimited = 1;
const LegacyTournamentTypeProgressive = 2; // This was never really used, but exists on the backend.
const LegacyTournamentTypeTurnBased = 3;
const LegacyTournamentTypeRematch = 4;
const PLAY_AGAIN_COUNT = 'currentPlayAgainButtonCountSKZ';

const gameId = SkillzModule.App.GameId;
const CASH_MINIMUM_AGE = 18;
const FRIENDS_MINIMUM_AGE = 13;

let isRematchTriggered = false;

const SKZReactVCManager = NativeModules.SKZReactViewControllerManager;
const SKZReactVersusManager = NativeModules.SKZReactVersusManager;

class PLOWDisplayHandler {
  displayResultsFromSubmitScore(submitScoreResponse: Object) {
    PlayerLevelUpController.fetchPlayerLevels();
    let match = new Match(submitScoreResponse);
    if (submitScoreResponse.tournamentList) {
      //Submit score for TB, old API Response, convert to REST structure;
      submitScoreResponse = this._RESTMatchStructFromOldAPIResponse(submitScoreResponse);
      match = new Match(submitScoreResponse);
      this.displayMatchResults(match, false, true);
    } else {
      this.checkForPostMatchRegistration(match);
    }
  }

  async checkForPostMatchRegistration(match: Match) {
    const tournamentStoreResponse = await TournamentStore.getTournamentById(match?.tournament_id);
    this.tournament = new Tournament(tournamentStoreResponse);

    if (this.tournament.shouldRegisterAfterPlay()) {
      CurrentUserStore.fetchUser(true).then((user: Object) => {
        if (!CurrentUserStore.isSavedAccount()) {
          NativeNavigation.displayPostMatchSkillzUI();
          const ageVerifyProps = {
            minimumAge:
              this.tournament.isFriendTournament() && this.tournament.isPointsTournament()
                ? FRIENDS_MINIMUM_AGE
                : CASH_MINIMUM_AGE,
            titleText: I18n.message('Enter Your Date of Birth'),
          };

          const saveAccountProps = {
            shouldCheckEmail: true,
            allowExit: true,
            allowSignIn: true,
            isAfterSubmitScore: true,
          };

          const emailVerifyProps = {
            shouldCheckEmailVerify: false,
          };
          SaveAccountFlowController.displaySaveAccountFlow(
            ageVerifyProps,
            saveAccountProps,
            emailVerifyProps,
            null,
            null,
            false,
            (success: boolean) => {
              if (success) {
                this.completeDisplayForSubmitScore(match);
              } else {
                NavigationManager.popNavigatorToRoot();
              }
            },
            true,
            match.id
          );
        } else {
          this.completeDisplayForSubmitScore(match);
        }
      });
    } else {
      this.completeDisplayForSubmitScore(match);
    }
  }

  completeDisplayForSubmitScore(match: Match) {
    CurrentUserStore.fetchUser(true)
      .then((user: Object) => {
        match.insertMatchIntoCoreData(user.id, true, () => {
          this.displayMatchResults(match, false, true);
        });
      })
      .catch((err: Object) => {
        console.debug(
          'PLOWDisplayHandler: failed call to user endpoint, forcing PLOW for android '
        );
        if (Platform.OS === 'android') {
          this.displayMatchResults(match, false, true);
        }
      });
  }

  displayResultsFromMatchStream(
    match: any,
    userId: any,
    animated: boolean,
    shouldPop: boolean,
    shouldShowRegularPLOW: boolean = true
  ) {
    match.leaderboardsUnlocked = true;
    const matchObj = new Match(match);
    this.displayMatchResults(
      matchObj,
      animated,
      false,
      userId,
      shouldPop,
      () => {},
      shouldShowRegularPLOW,
      true
    );
  }

  displayResultsFromChat(match: Match, userId: Number, shouldPop: boolean, onDismiss: () => void) {
    match.leaderboardsUnlocked = true;
    const matchObj = new Match(match);
    this.displayMatchResults(matchObj, true, false, userId, shouldPop, onDismiss);
  }

  displayResultsFromDeepLink(data: any) {
    MatchStore.fetchMatch(data.matchId, data.userId, false, (payload, error) => {
      if (!error) {
        const match = new Match(payload);
        match.insertMatchIntoCoreData(data.userId, true, () => {
          isRematchTriggered = data.rematchTriggered;
          this.displayMatchResults(match, true, false);
        });
      }
    });
  }

  async displayMatchResults(
    match: Match,
    animated: boolean,
    isSubmittingScore: boolean,
    userId: string,
    shouldPop: boolean,
    onDismiss: () => void,
    shouldShowRegularPLOW: boolean = true,
    isFromMatchStream: boolean = false
  ): Promise {
    if (Sizes.isGameLandscape()) {
      GlobalEventEmitter.emit('toggleShouldRender', false);
    }
    if (!isSubmittingScore) {
      Prefs.set({[PLAY_AGAIN_COUNT]: 0});
    }

    const mainUserId = await CurrentUserStore.getCurrentUserId();
    const type = match.type;
    const finished_at = match.finished_at;
    const id = parseInt(match.id);
    let isAborted = false;

    if (Array.isArray(match.players)) {
      match.players.forEach((player) => {
        if (player.state == 'ABORTED') {
          isAborted = true;
        }
      });
    }

    const completion = () => {
      if (isSubmittingScore) {
        setTimeout(() => {
          NativeNavigation.displayPostMatchSkillzUI();
        }, SKZConstants.MS_PER_HALF_SEC);
      }
    };

    const tournamentStoreResponse = await TournamentStore.getTournamentById(match?.tournament_id);
    const tournamentFromMatch = new Tournament(tournamentStoreResponse);

    // For 3+ player H2H tournaments with the Client Prop on, use EventDetailsScreen UI just like Live Events
    if (
      (type == this.matchTypeStringFromEnum(Tournament.Type.UNLIMITED_ENTRY) && !match.bracket) ||
      tournamentFromMatch?.isThreePlusH2H()
    ) {
      // Hosted Live Events: only navigate to live-event URL when we have a valid live_event id.
      // Practice matches (and some async multi) have no live_event, so live_event?.id is undefined
      // and we must use the fallback (LiveEventCompletedPLOW etc.) to avoid stuck loading screen.
      const liveEventId = tournamentFromMatch?.live_event?.id;
      if (ClientPropertyConstants.getLiveEventHostedEnabled() && liveEventId != null) {
        SkillzRouter.replace(`/games/${gameId}/live-event/${liveEventId}`, {
          userId: userId || mainUserId,
          mainUserId,
          isSubmittingScore,
          shouldPop,
          match,
          tournamentSource: tournamentFromMatch,
        });
        completion();
        return;
      }
      //Unlimited, check if completed
      if (finished_at) {
        //Completed
        NavigationManager.push(
          <LiveEventCompletedPLOW match={match} />,
          {scene: 'LiveEventCompletedPLOW'},
          !isSubmittingScore
        );
      } else if (match.hasOwnProperty('lastScoreString')) {
        //Pending
        //Temporary block for release without PLOW rebuilds
        NavigationManager.push(
          <LiveEventRecapPLOW match={match} tournament={this.tournament} />,
          {scene: 'LiveEventRecapPLOW'},
          !isSubmittingScore
        );
      } else {
        NavigationManager.push(
          <LiveEventPendingPLOW match={match} mainUserId={mainUserId} userId={userId} />,
          {scene: 'LiveEventPendingPLOW'},
          !isSubmittingScore
        );
      }
      completion();
    } else if (
      type == this.matchTypeStringFromEnum(Tournament.Type.LIMITED_ENTRY) ||
      type == this.matchTypeStringFromEnum(Tournament.Type.REMATCH) ||
      match.bracket ||
      (isAborted && type == 'TURN_BASED')
    ) {
      // Limited: disable native progress modal for entire result flow so it never blocks the result screen.
      // Set flag first, then dismiss any already-visible dialog (e.g. shown when returning from game or during league sync).
      if (typeof SkillzModule?.setSkipProgressModalForPostMatchResult === 'function') {
        SkillzModule.setSkipProgressModalForPostMatchResult(true);
      }
      if (typeof SkillzModule?.dismissProgressModalDialog === 'function') {
        SkillzModule.dismissProgressModalDialog();
      }
      LoadingIndicator.forceHide();
      if (match.isDeclinedRematch(mainUserId)) {
        ModalNavigationController.showModal(<RematchDeclinedModal />, {
          animationType: 'fade',
          transparent: true,
        });
      } else {
        if (isSubmittingScore) {
          GlobalEventEmitter.emit('toggleShouldRender', false);
        }
        /**
         * TODO:
         * Here for Live Bracket, shouldShowRegularPLOW is true and match.isLiveBracket() is returning false
         * Ideally, it should be vice-versa. Check why it is happening.
         * Problem: If we show the regular PLOW screen for live bracket, then the autoplay functionality is not there.
         */
        if (match.isLiveBracket() && tournamentFromMatch && !shouldShowRegularPLOW) {
          // Post-match only: skip showing native loader and dismiss any visible one
          if (typeof SkillzModule?.setSkipProgressModalForPostMatchResult === 'function') {
            SkillzModule.setSkipProgressModalForPostMatchResult(true);
          }
          if (typeof SkillzModule?.dismissProgressModalDialog === 'function') {
            SkillzModule.dismissProgressModalDialog();
          }
          LoadingIndicator.forceHide();
          SkillzRouter.push(`/games/${gameId}/brackets-results/${tournamentFromMatch.id}`, {
            match,
            hideOrientationFlip: true,
            userId: userId || mainUserId,
            mainUserId,
            tournament: tournamentFromMatch,
          });
        } else {
          let leagueInterstitialProps = null;
          /*
           * Fetch Leagues data and determine which leagues modal (status or instant promotion) to be shown to the user.
           * Only trigger this on score submit Results Screen for H2H or sync matches.
           * Only trigger if we're using Leagues v3
           */
          if (
            ClientPropertyConstants.getLeaguesV3Enabled() &&
            ClientPropertyConstants.getLeaguesStatusInterstitialEnabled() &&
            LeagueFTUEController.getHasSeenAppropriateProgressiveLeagueFTUEByVersion() &&
            isSubmittingScore &&
            !isRematchTriggered &&
            Platform.OS !== 'web' &&
            this.tournament &&
            !this.tournament?.isBracket() &&
            !this.tournament?.live_event &&
            !this.tournament?.isThreePlusH2H()
          ) {
            try {
              leagueInterstitialProps = await handleLeagueInterstitialsAndSync();
            } catch (error) {
              console.error('Error handling league interstitial data: ', error);
            }
          }
          /**
           * Reset to the home screen before submitting the score for live bracket results.
           * This ensures that any existing Regular PLOW screen is removed from the navigation stack.
           * If Regular PLOW remains in the stack, React Native will reuse the existing screen instance.
           * Since Regular PLOW updates its state only on mount, this would cause stale data to be displayed.
           */
          if (isSubmittingScore && tournamentFromMatch?.isLiveBracket()) {
            SkillzSDK.resetToHomeScreen();
          }

          // Post-match only: skip showing native loader and dismiss any visible one
          if (typeof SkillzModule?.setSkipProgressModalForPostMatchResult === 'function') {
            SkillzModule.setSkipProgressModalForPostMatchResult(true);
          }
          if (typeof SkillzModule?.dismissProgressModalDialog === 'function') {
            SkillzModule.dismissProgressModalDialog();
          }
          LoadingIndicator.forceHide();
          SkillzRouter.push(
            `/games/${gameId}/tournament/${tournamentFromMatch.id}/result/${match.id}`,
            {
              match,
              isSubmittingScore,
              mainUserId,
              userId,
              shouldPop,
              tournament: tournamentFromMatch,
              isRematch: isRematchTriggered,
              leagueInterstitialProps,
              forcePortrait: ClientPropertyConstants.getSync1vNEnabled(),
              isFromMatchStream,
            }
          );
        }

        isRematchTriggered = false;
      }

      completion();
    } else if (type == Tournament.Type.TURN_BASED) {
      // Do nothing, no longer supported.
    } else {
      console.debug('No match found, display home: ' + JSON.stringify(match));
      GlobalEventEmitter.emit('toggleShouldRender', true);
      completion(false);
    }
  }

  _RESTMatchStructFromOldAPIResponse(oldAPIResponse): Match {
    const oldTournament = oldAPIResponse.tournamentList[0].tournament;
    const match = {
      id: oldTournament.id.toString(),
      is_rematch: oldTournament.rematch,
      expires_at: oldTournament.expire,
      finished_at: oldTournament.dateEnded,
      type: this.matchTypeStringFromLegacyEnum(oldTournament.type),
      tournament_id: oldTournament.templateId,
      name: oldTournament.name,
      lastScoreString: oldAPIResponse.lastScoreString,
      leaderboardsUnlocked: oldAPIResponse.leaderboardsUnlocked,
      bracket: oldTournament.bracket,
      cash_fee: oldTournament.entryFee,
      z_fee: oldTournament.entryPoints,
      tie_number: oldTournament.tieNumber,
      matchIsFinished: oldAPIResponse.matchIsFinished,
    };

    if (oldTournament.bracket && match.bracket) {
      match.bracket.expires_at = oldTournament.bracket.expiresAt;
    }

    match.share = {
      subject: oldAPIResponse.shareSubject,
      url: oldAPIResponse.shareURL,
      replay_url: oldAPIResponse.shareURL,
      message: oldAPIResponse.shareMessage,
    };

    const oldScoreMessages = oldAPIResponse.tournamentList[0].scoreMessages;
    if (oldScoreMessages) {
      match.scoreMessages = {
        last_entry: oldScoreMessages.lastEntry,
        your: oldScoreMessages.your,
        best: oldScoreMessages.best,
        in_progress: oldScoreMessages.in_progress,
      };
    }

    if (oldTournament.event) {
      match.share.message = oldTournament.event.shareMessage;
    }

    let turnInformation;
    if (oldAPIResponse.turnInformation) {
      turnInformation = oldAPIResponse.turnInformation;
    } else if (oldAPIResponse.turnInformationList) {
      turnInformation = oldAPIResponse.turnInformationList[0];
    }

    if (turnInformation) {
      match.turns = turnInformation.roundResults;
      match.turn_based_type = turnInformation.turnBasedType;
    }

    match.players = [];
    const oldPlayerList = oldAPIResponse.tournamentList[0].playerMetrics;
    if (oldPlayerList instanceof Array) {
      oldPlayerList.forEach((oldPlayer) => {
        let playerScore = 0;
        if (oldPlayer.metrics instanceof Array) {
          oldPlayer.metrics.forEach((metric) => {
            if (metric.name == 'score') {
              playerScore = metric.value;
            }
          });
        }

        const newPlayer = {
          id: oldPlayer.entryId,
          outcome: oldPlayer.outcome,
          state: this.playerStatusStringFromEnum(oldPlayer.status),
          shoutout: oldPlayer.tauntText,
          position: oldPlayer.position,
          avatar_url: oldPlayer.profilePictureUrl,
          flag_url: oldPlayer.flagUrl,
          user_id: oldPlayer.secUserId,
          cash_prize: oldPlayer.prize,
          z_prize: oldPlayer.prizePoints,
          custom_prize: oldPlayer.customPrize,
          ticketz_prize: oldPlayer.prizeTicketz,
          score: playerScore,
          thumbnail_url: oldPlayer.thumbnailUrl,
          name: oldPlayer.username,
          bestScore: playerScore,
        };

        if (oldPlayer.hasReplay) {
          newPlayer.replay_url = oldPlayer.replayUrl;
        }

        match.players.push(newPlayer);
      });
    }

    return match;
  }

  //No idea why 3 was skipped.
  roundOutcomeEnumFromString(outcomeString: string): number {
    switch (outcomeString) {
      case 'LOSS':
        return 0;
      case 'WIN':
        return 1;
      case 'DRAW':
        return 2;
      default:
        return 4; // eslint-disable-line no-magic-numbers
    }
  }

  matchTypeStringFromEnum(typeEnum: number): string {
    switch (typeEnum) {
      case Tournament.Type.REMATCH:
        return 'REMATCH';
      case Tournament.Type.TURN_BASED:
        return 'TURN_BASED';
      case Tournament.Type.UNLIMITED_ENTRY:
        return 'UNLIMITED_ENTRY';
      case Tournament.Type.LIMITED_ENTRY:
      default:
        return 'LIMITED_ENTRY';
    }
  }

  //This function purposefully uses Legacy types and SHOULD NOT BE CHANGED.
  matchTypeStringFromLegacyEnum(typeEnum: Number): string {
    switch (typeEnum) {
      case LegacyTournamentTypeRematch:
        return 'REMATCH';
      case LegacyTournamentTypeTurnBased:
        return 'TURN_BASED';
      case LegacyTournamentTypeUnlimited:
        return 'UNLIMITED_ENTRY';
      case LegacyTournamentTypeLimited:
      default:
        return 'LIMITED_ENTRY';
    }
  }

  playerStatusStringFromEnum(statusEnum: number): string {
    switch (statusEnum) {
      case MatchPlayer.Status.ABORTED:
        return 'ABORTED';
      case MatchPlayer.FINISHED:
        return 'FINISHED';
      case MatchPlayer.STARTED:
      default:
        return 'STARTED';
    }
  }
}

export default new PLOWDisplayHandler();
